import React, { useEffect, useState } from 'react';
import { useRouter } from '@abyss/ui/router/hooks/useRouter';
import { useLocation } from '@abyss/ui/router/hooks/useLocation';
import { Header } from 'src/common/Header';
import { Switch } from '@abyss/ui/router/Switch';
import { Route } from '@abyss/ui/router/Route';
import { NavList } from '@abyss/ui/router/NavList';
import { AlertBanner } from '@abyss/ui/base/AlertBanner';
import { useUserInfo } from 'src/hooks/useUserInfo';
import { useUserPermissions } from 'src/hooks/useUserPermissions';
import { useAdminContext } from 'src/hooks/useAdminContext';
import { AlertContext } from 'src/tools/AlertContext';
import { getMessage } from 'src/tools/messages';
import { OverlayProvider } from '@abyss/web/ui/OverlayProvider';
import { getFeature } from 'src/tools/features';
import { ThemeProvider } from '@abyss/web/ui/ThemeProvider';
import { createTheme } from '@abyss/web/tools/theme';
import { config } from '@abyss/web/tools/config';
import { ServiceUnavailable } from 'src/common/ServiceUnavailable';
import { LoadingOverlay } from '@abyss/web/ui/LoadingOverlay';
import { styled } from '@abyss/web/tools/styled';
import { routes } from './routes';
import { Redirect } from './Redirect';
import { IdleUser } from '../../common/IdleUser/IdleUser';

const LoadingDiv = styled('div', {
  margin: '100px auto',
});

export const App = () => {
  const router = useRouter();
  const { pathname } = useLocation();
  const [alerts, setAlerts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const addAlert = alert => {
    const alertCopy = { ...alert };
    const code = alert.code || alert.status;
    let message;
    if (code) {
      message = getMessage(`${alert.id}[${code}]`);
      if (!message) {
        message = getMessage(`default-error[${code}]`);
      }
    }
    if (message) {
      alertCopy.message = message;
    }
    setAlerts([...alerts, alertCopy]);
  };

  const [userPermissionsResult, getUserPermissions] = useUserPermissions({
    onCompleted: () => {
      setIsLoading(false);
    },
    onError: error => {
      if (error?.status && error?.status < 500) {
        addAlert(error);
      }
      setIsLoading(false);
    },
  });

  const [, getUserInfo] = useUserInfo({
    onCompleted: () => {
      getUserPermissions();
    },
    onError: error => {
      addAlert(error);
    },
  });
  const [, setAdminContext] = useAdminContext({
    onCompleted: () => {
      getUserInfo();
    },
  });

  const userPermissions = userPermissionsResult.data;
  const roles = userPermissions.admin
    ? userPermissions.admin.userRoles
    : userPermissions.userRoles;
  const APP_ENV = config('APP_ENV');
  const filteredRoutes = routes.filter(route => {
    if (APP_ENV === 'local') {
      return true;
    }
    if (route.toggle) {
      return getFeature(route.toggle) && roles[route.role];
    }
    return roles[route.role];
  });

  useEffect(() => {
    setAdminContext();
  }, []);

  useEffect(() => {
    if (filteredRoutes.length) {
      const defaultRoute = filteredRoutes.find(r => r.path === pathname);
      if (!defaultRoute) {
        router.replace(filteredRoutes[0].path);
      }
    }
  }, [filteredRoutes]);

  const removeAlert = index => {
    const newList = alerts.filter((x, i) => i !== index);
    if (newList.length) {
      newList.forEach(alert => {
        addAlert(alert);
      });
    } else {
      setAlerts([]);
    }
  };

  const theme = createTheme('uhc');

  return (
    <ThemeProvider theme={theme}>
      <AlertContext.Provider value={{ addAlert }}>
        <OverlayProvider>
          <IdleUser />
          <Header />
          <AlertBanner alerts={alerts} onClose={index => removeAlert(index)} />
          <Choose>
            <When condition={userPermissionsResult.error && !isLoading}>
              <ServiceUnavailable />
            </When>
            <When condition={!isLoading}>
              <Switch title="{{title}} | Technical User">
                <Route path="/redirect/:token" component={Redirect} />
                <Route
                  render={() => (
                    <NavList routes={filteredRoutes} topOffset={44} />
                  )}
                />
              </Switch>
            </When>
            <Otherwise>
              <LoadingDiv>
                <LoadingOverlay
                  isLoading={isLoading}
                  loadingTitle="Please wait"
                  loadingMessage="while we retrieve user permissions"
                />
              </LoadingDiv>
            </Otherwise>
          </Choose>
        </OverlayProvider>
      </AlertContext.Provider>
    </ThemeProvider>
  );
};
